import { Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ReuseInput } from "../../ReuseInput";
import { FileDropZone } from "../../FileDropZone";
import { FileList } from "../../FileList";
import { FeedbackMessages } from "../../../../types/apiTypes";

type Errors = { [key: string]: boolean };

const initialValues: FeedbackMessages = {
  functionality_suggestion: "",
  feedback: "",
};

const validation: { [key: string]: (value: string) => boolean } = {
  functionality_suggestion: () => false,
  feedback: (value: string) => !value,
};

const SUPPORTS_FILES = [
  "csv",
  "xls",
  "xlsx",
  "mp4",
  "txt",
  "docx",
  "jpg",
  "jpeg",
  "png",
  "pdf",
];

export const FormBody = ({
  isShowed,
  setFiles,
  setFeedbackMessages,
  filesWithError,
  setIsError,
  isLoading,
}: {
  isShowed: boolean;
  setFiles: (files: File[]) => void;
  setIsError: (isError: boolean) => void;
  setFeedbackMessages: (messages: FeedbackMessages) => void;
  filesWithError?: string[];
  isLoading: boolean;
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState<FeedbackMessages>(initialValues);

  const [error, setError] = useState<Errors>({
    functionality_suggestion: false,
    feedback: false,
  });

  useEffect(() => {
    setIsError(Object.values(error).some((err) => err));
  }, [error]);

  useEffect(() => {
    setFiles(uploadedFiles);
  }, [uploadedFiles]);

  useEffect(() => {
    setFeedbackMessages(formData);
  }, [formData]);

  const inputChangeHandler = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({
      ...prev,
      [field]: validation[field](value),
    }));
  };

  const setErrorHandler = (field: string, value: boolean) => {
    setError((prev) => ({ ...prev, [field]: value }));
  };

  const onFileUpload = (files: File[]) => {
    setUploadedFiles([...uploadedFiles, ...files]);
  };

  const removeFileHandler = (name: string) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.name !== name));
  };

  if (!isShowed) return null;

  return (
    <>
      <Stack gap="24px">
        <Stack gap="12px">
          <Typography
            component="span"
            variant="subtitle1"
            color="custom.textDarkBlue"
            fontWeight={500}
          >
            What new functionality would improve your workflow the most?
          </Typography>
          <ReuseInput
            isDisabled={isLoading}
            name="functionality_suggestion"
            handleInputChange={inputChangeHandler}
            errorMessage={""}
            value={formData.functionality_suggestion || ""}
            label=""
            maxLength={1000}
            placeholder="Describe the feature you'd love to see..."
            isError={error.functionality_suggestion}
            setErrorHandler={setErrorHandler}
            multiline={true}
          />
        </Stack>
        <Stack gap="12px">
          <Typography
            component="span"
            variant="subtitle1"
            color="custom.textDarkBlue"
            fontWeight={500}
          >
            Share your feedback, idea or bug report *
          </Typography>
          <ReuseInput
            isDisabled={isLoading}
            name="feedback"
            handleInputChange={inputChangeHandler}
            errorMessage={""}
            value={formData.feedback || ""}
            label=""
            maxLength={1000}
            placeholder="Share your thoughts or describe the issue..."
            isError={error.feedback}
            setErrorHandler={setErrorHandler}
            multiline={true}
          />
        </Stack>
      </Stack>

      <Stack gap="12px" paddingBottom="16px">
        <Stack gap="8px">
          <Typography
            component="span"
            variant="subtitle1"
            color="custom.textDarkBlue"
            fontWeight={500}
          >
            Upload a file or screenshot to help illustrate your feedback
          </Typography>
          <Stack>
            <Typography
              component="span"
              variant="body1"
              color="custom.textDarkBlue"
            >
              Supports: {SUPPORTS_FILES.join(", ")}
            </Typography>
            <Typography
              component="span"
              variant="body1"
              color="custom.textDarkBlue"
            >
              Maximum file size: 10MB
            </Typography>
          </Stack>
        </Stack>
        {uploadedFiles.length < 10 && (
          <FileDropZone
            isDisabled={isLoading}
            maxFiles={10}
            maxSizeMB={10}
            uploadedFiles={uploadedFiles}
            onFileUpload={onFileUpload}
            acceptedExtensions={SUPPORTS_FILES}
          />
        )}

        <FileList
          filesWithError={filesWithError}
          maxHeight="136px"
          isDisabled={isLoading}
          files={uploadedFiles}
          removeFileHandler={removeFileHandler}
          acceptedExtensions={SUPPORTS_FILES}
        />
      </Stack>
    </>
  );
};
